import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSearch, AlertTriangle, CheckCircle, FileX, ChevronDown, ChevronRight, Loader2, Building2, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

interface MissingDoc {
  name: string;
  category: string;
  description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
}

interface ClientResult {
  clientId: string;
  clientName: string;
  entityType: string;
  financialYear: string;
  documentsOnFile: number;
  requiredDocuments: MissingDoc[];
  missingDocuments: MissingDoc[];
  foundDocuments: { name: string; category: string; matchedFile?: string }[];
  completeness: number;
}

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-500/10 text-red-500 border-red-500/20',
  high: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  medium: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  low: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20',
};

export default function MissingDocumentsPage() {
  const [expandedClient, setExpandedClient] = useState<string | null>(null);

  const { data: results, isLoading } = useQuery({
    queryKey: ['missing-documents-all'],
    queryFn: async () => {
      const r = await apiClient.get('/clients/missing-documents/all');
      return r.data.data as ClientResult[];
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Scanning all clients for missing documents...</p>
        </div>
      </div>
    );
  }

  const clients = results || [];
  const totalMissing = clients.reduce((sum, c) => sum + c.missingDocuments.length, 0);
  const criticalMissing = clients.reduce((sum, c) => sum + c.missingDocuments.filter(d => d.priority === 'critical').length, 0);
  const avgCompleteness = clients.length > 0 ? Math.round(clients.reduce((sum, c) => sum + c.completeness, 0) / clients.length) : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={FileSearch}
        title="Missing Document Detector"
        description="Identifies required documents that are missing from each client's file"
      />


      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{clients.length}</p>
                <p className="text-xs text-muted-foreground">Clients Scanned</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <FileX className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalMissing}</p>
                <p className="text-xs text-muted-foreground">Missing Documents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{criticalMissing}</p>
                <p className="text-xs text-muted-foreground">Critical Missing</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Shield className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{avgCompleteness}%</p>
                <p className="text-xs text-muted-foreground">Avg Completeness</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Client List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Client Document Status</CardTitle>
          <CardDescription>Click a client to see missing document details</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {clients.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileSearch className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No clients found. Add clients first.</p>
            </div>
          ) : (
            <div className="divide-y">
              {clients.sort((a, b) => a.completeness - b.completeness).map((client) => {
                const isExpanded = expandedClient === client.clientId;
                const hasCritical = client.missingDocuments.some(d => d.priority === 'critical');

                return (
                  <div key={client.clientId}>
                    <button
                      onClick={() => setExpandedClient(isExpanded ? null : client.clientId)}
                      className="w-full flex items-center gap-4 px-4 py-3 hover:bg-muted/30 transition-colors text-left"
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{client.clientName}</span>
                          <Badge variant="outline" className="text-[9px]">{client.entityType}</Badge>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          {client.documentsOnFile} files on record, FY{client.financialYear}
                        </p>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        {client.missingDocuments.length > 0 ? (
                          <div className="flex items-center gap-1.5">
                            {hasCritical && <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
                            <span className={cn('text-xs font-medium', hasCritical ? 'text-red-500' : 'text-amber-500')}>
                              {client.missingDocuments.length} missing
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                            <span className="text-xs text-green-500 font-medium">Complete</span>
                          </div>
                        )}
                        <div className="w-20">
                          <Progress
                            value={client.completeness}
                            className={cn('h-1.5',
                              client.completeness < 50 ? '[&>div]:bg-red-500' :
                              client.completeness < 80 ? '[&>div]:bg-amber-500' : ''
                            )}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground w-8 text-right">{client.completeness}%</span>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 bg-muted/10">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                          {/* Missing */}
                          <div>
                            <h4 className="text-xs font-medium text-red-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                              <FileX className="h-3 w-3" /> Missing ({client.missingDocuments.length})
                            </h4>
                            {client.missingDocuments.length === 0 ? (
                              <p className="text-xs text-muted-foreground italic">All required documents found</p>
                            ) : (
                              <div className="space-y-1.5">
                                {client.missingDocuments.map((doc, i) => (
                                  <div key={i} className="flex items-start gap-2 text-xs p-2 rounded bg-background border">
                                    <FileX className="h-3 w-3 text-red-400 mt-0.5 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5">
                                        <span className="font-medium">{doc.name}</span>
                                        <Badge variant="outline" className={cn('text-[8px] px-1 py-0', PRIORITY_COLORS[doc.priority])}>
                                          {doc.priority}
                                        </Badge>
                                      </div>
                                      <p className="text-muted-foreground mt-0.5">{doc.description}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Found */}
                          <div>
                            <h4 className="text-xs font-medium text-green-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" /> Found ({client.foundDocuments.length})
                            </h4>
                            <div className="space-y-1">
                              {client.foundDocuments.map((doc, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded">
                                  <CheckCircle className="h-3 w-3 text-green-400 shrink-0" />
                                  <span className="text-muted-foreground">{doc.name}</span>
                                  {doc.matchedFile && (
                                    <span className="text-[10px] text-muted-foreground truncate ml-auto max-w-[200px]" title={doc.matchedFile}>
                                      {doc.matchedFile}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
